import { useEffect, useState } from "react";
import axios from "axios";
import {
  Clock,
  RefreshCw,
  AlertTriangle,
  Sparkles,
  TrendingUp,
  Flame,
  Search,
  ShoppingBag,
  Info,
  Zap,
} from "lucide-react";
import { LocationIndicator } from "./LocationIndicator";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section C de la Veille Proactive — Signaux faibles et tendances territoriales.
 *
 * SOURCE SECONDAIRE complémentaire (Google Trends). Visuellement plus discrète
 * que les Sections A/B pour matérialiser ce positionnement.
 *
 * 3 widgets indépendants :
 * - Widget 1 : Signaux faibles santé locale (top 5 recherches en hausse)
 * - Widget 2 : Buzz produit OTC national (alertes hausse > +50%)
 * - Widget 3 : Veille concurrentielle OTC régionale (4 catégories parapharmacie)
 *
 * Branchée sur GET /api/veille-proactive/tendances-locales (étape 5).
 *
 * 5 états : loading / live / partiellement disponible / empty / unavailable.
 * Comme aux autres sections : skeleton premium, jamais d'erreur brute.
 */
export function TendancesSection() {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("benchory_token");
      const resp = await axios.get(`${API_URL}/veille-proactive/tendances-locales`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000,
      });
      setPayload(resp.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[veille-proactive] Tendances fetch error:", e?.message);
      setPayload({
        data: { location: {}, signaux_faibles: [], buzz_national: [], veille_concurrentielle: [] },
        meta: { status: "unavailable", user_message: null },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      className="space-y-6"
      data-testid="tendances-section"
      aria-labelledby="tendances-title"
    >
      <SectionHeader
        meta={payload?.meta}
        location={payload?.data?.location}
        loading={loading}
        onRefresh={() => {
          setLoading(true);
          fetchData();
        }}
      />

      {loading && <TendancesSkeleton />}

      {!loading && payload && (
        <TendancesContent data={payload.data || {}} meta={payload.meta || {}} />
      )}
    </section>
  );
}


// ── En-tête ───────────────────────────────────────────────────────────────────


function SectionHeader({ meta, location, loading, onRefresh }) {
  return (
    <header className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={14} className="text-cabinet-gold" strokeWidth={1.7} />
            <span className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-cabinet-mute-1">
              Source secondaire · Google Trends
            </span>
          </div>
          <h2
            id="tendances-title"
            className="font-playfair text-2xl lg:text-3xl text-cabinet-ink font-medium tracking-tight"
          >
            Signaux faibles et tendances territoriales
          </h2>
          <p className="mt-1.5 text-cabinet-navy text-[14px] leading-relaxed max-w-2xl">
            Sujets santé qui montent dans votre département, produits OTC en
            buzz national et indices de demande sur les catégories
            parapharmacie de votre région.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex-shrink-0 text-cabinet-mute-1 hover:text-cabinet-bordeaux transition-colors p-2 rounded-md disabled:opacity-40"
          data-testid="tendances-refresh"
          aria-label="Actualiser les tendances"
        >
          <RefreshCw size={16} strokeWidth={1.8} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {location?.dept && <LocationIndicator location={location} />}
        {meta?.cached_at && (
          <span
            className="inline-flex items-center gap-1.5 text-[11.5px] tracking-wide text-cabinet-mute-1"
            data-testid="tendances-cached-at"
          >
            <Clock size={11} strokeWidth={1.8} />
            Données du {_formatDateTime(meta.cached_at)}
          </span>
        )}
      </div>

      {meta?.user_message && (
        <div
          className="inline-flex items-start gap-2 px-3.5 py-2 rounded-md bg-cabinet-cream-warm border border-cabinet-gold/30 text-[12.5px] text-cabinet-ink max-w-2xl"
          data-testid="tendances-message-banner"
        >
          <Info size={13} className="flex-shrink-0 mt-0.5 text-cabinet-gold" strokeWidth={1.8} />
          <span>{meta.user_message}</span>
        </div>
      )}
    </header>
  );
}


// ── Skeleton ──────────────────────────────────────────────────────────────────


function TendancesSkeleton() {
  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-3 gap-4"
      data-testid="tendances-skeleton"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="bg-white border border-cabinet-mute-2/40 rounded-lg p-5 animate-pulse"
        >
          <div className="h-3 w-1/3 rounded bg-cabinet-mute-2/40 mb-3" />
          <div className="h-5 w-2/3 rounded bg-cabinet-mute-2/55 mb-4" />
          <div className="space-y-2.5">
            {[0, 1, 2, 3].map((s) => (
              <div key={s} className="h-3 rounded bg-cabinet-mute-2/30" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// ── Contenu (5 états) ────────────────────────────────────────────────────────


function TendancesContent({ data, meta }) {
  const status = meta?.status;
  const unavailableWidgets = new Set(meta?.unavailable_widgets || []);

  if (status === "unavailable") {
    return (
      <PremiumEmptyState
        icon={AlertTriangle}
        title="La détection de signaux faibles est temporairement indisponible"
        message="Cette source secondaire (Google Trends) repose sur un endpoint non documenté qui peut connaître des interruptions. Les analyses principales de votre Veille Proactive restent fiables."
        testId="tendances-empty-unavailable"
      />
    );
  }

  if (status === "empty") {
    return (
      <PremiumEmptyState
        icon={Sparkles}
        title="Aucun signal faible détecté ce cycle"
        message="La situation est calme dans votre zone. Les indicateurs sont actualisés quotidiennement et reprendront vie dès qu'un signal pertinent émergera."
        testId="tendances-empty-no-signal"
      />
    );
  }

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-3 gap-4"
      data-testid="tendances-widgets-grid"
    >
      <SignauxFaiblesWidget
        signals={data.signaux_faibles || []}
        unavailable={unavailableWidgets.has("signaux")}
      />
      <BuzzNationalWidget
        alerts={data.buzz_national || []}
        unavailable={unavailableWidgets.has("buzz")}
      />
      <VeilleConcurrentielleWidget
        items={data.veille_concurrentielle || []}
        unavailable={unavailableWidgets.has("veille")}
      />
    </div>
  );
}


// ── Widget 1 : Signaux faibles santé locale ──────────────────────────────────


function SignauxFaiblesWidget({ signals, unavailable }) {
  return (
    <WidgetCard
      kicker="Recherches santé en hausse"
      title="Signaux faibles"
      subtitle="Top 5 sur 7 jours dans votre département"
      icon={Search}
      testId="widget-signaux-faibles"
      unavailable={unavailable}
    >
      {signals.length === 0 ? (
        <WidgetEmpty message="Aucun signal faible identifié dans votre département cette semaine." />
      ) : (
        <ol className="space-y-2.5" data-testid="signaux-faibles-list">
          {signals.map((sig, i) => (
            <li
              key={`${sig.query}-${i}`}
              className="flex items-baseline gap-3 text-[13.5px]"
            >
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cabinet-bordeaux/8 flex items-center justify-center text-[10.5px] font-semibold text-cabinet-bordeaux">
                {sig.rank}
              </span>
              <span className="flex-1 text-cabinet-ink leading-snug">{sig.query}</span>
              {sig.is_breakout ? (
                <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.15em] font-semibold text-cabinet-gold">
                  <Flame size={11} strokeWidth={2} />
                  Émergence
                </span>
              ) : sig.rising_pct != null ? (
                <span className="flex-shrink-0 inline-flex items-center gap-1 text-[12px] font-medium text-cabinet-bordeaux">
                  <TrendingUp size={11} strokeWidth={2} />
                  +{sig.rising_pct}%
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </WidgetCard>
  );
}


// ── Widget 2 : Buzz produit OTC national ─────────────────────────────────────


function BuzzNationalWidget({ alerts, unavailable }) {
  return (
    <WidgetCard
      kicker="Niveau national"
      title="Buzz produit OTC"
      subtitle="Recherches en hausse > +50% sur 7 jours"
      icon={Flame}
      testId="widget-buzz-national"
      unavailable={unavailable}
    >
      {alerts.length === 0 ? (
        <WidgetEmpty message="Aucun produit OTC en buzz significatif cette semaine au niveau national." />
      ) : (
        <ul className="space-y-3" data-testid="buzz-national-list">
          {alerts.map((a, i) => (
            <li
              key={`${a.keyword}-${i}`}
              className="border-l-2 border-cabinet-bordeaux/30 pl-3"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-playfair text-[15px] text-cabinet-ink font-medium tracking-tight">
                  {a.keyword}
                </span>
                <span className="text-[12px] font-medium text-cabinet-bordeaux tracking-wide">
                  +{Math.round(a.trend_pct)}%
                </span>
              </div>
              <p className="mt-0.5 text-[11.5px] text-cabinet-mute-1 tracking-wide">
                Volume 7&nbsp;j {a.avg_7d?.toFixed?.(0) ?? "—"} vs baseline 30&nbsp;j {a.avg_1m?.toFixed?.(0) ?? "—"}
              </p>
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}


// ── Widget 3 : Veille concurrentielle OTC régionale ──────────────────────────


function VeilleConcurrentielleWidget({ items, unavailable }) {
  const maxIndex = Math.max(
    100,
    ...items.flatMap((i) => [i.demand_index || 0, i.national_index || 0])
  );

  return (
    <WidgetCard
      kicker="Indices de demande"
      title="Veille concurrentielle OTC"
      subtitle="Votre région vs niveau national"
      icon={ShoppingBag}
      testId="widget-veille-concurrentielle"
      unavailable={unavailable}
    >
      {items.length === 0 ? (
        <WidgetEmpty message="Pas assez de données pour comparer votre région au niveau national cette semaine." />
      ) : (
        <ul className="space-y-3.5" data-testid="veille-concurrentielle-list">
          {items.map((item) => (
            <VeilleConcItem key={item.category} item={item} maxIndex={maxIndex} />
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}


function VeilleConcItem({ item, maxIndex }) {
  const region = item.demand_index || 0;
  const national = item.national_index || 0;
  const regionPct = (region / maxIndex) * 100;
  const nationalPct = (national / maxIndex) * 100;
  const delta = item.delta || 0;
  const deltaPositive = delta > 0;
  return (
    <li data-testid={`veille-conc-item-${item.category}`}>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <span className="text-[13px] font-medium text-cabinet-ink tracking-tight">
          {item.label}
        </span>
        <span
          className={`text-[11.5px] font-medium tracking-wide ${
            deltaPositive ? "text-cabinet-bordeaux" : "text-cabinet-mute-1"
          }`}
        >
          {deltaPositive ? "+" : ""}
          {Math.round(delta)} pts
        </span>
      </div>
      <div className="space-y-1">
        {/* Région */}
        <div className="flex items-center gap-2">
          <span className="w-12 text-[10.5px] uppercase tracking-[0.15em] text-cabinet-mute-1 flex-shrink-0">
            Zone
          </span>
          <div className="flex-1 h-1.5 rounded-sm bg-cabinet-mute-2/35 overflow-hidden">
            <div
              className="h-full bg-cabinet-bordeaux/70 rounded-sm"
              style={{ width: `${regionPct}%` }}
            />
          </div>
          <span className="w-7 text-[11px] text-cabinet-ink text-right flex-shrink-0">
            {Math.round(region)}
          </span>
        </div>
        {/* National */}
        <div className="flex items-center gap-2">
          <span className="w-12 text-[10.5px] uppercase tracking-[0.15em] text-cabinet-mute-1 flex-shrink-0">
            France
          </span>
          <div className="flex-1 h-1.5 rounded-sm bg-cabinet-mute-2/35 overflow-hidden">
            <div
              className="h-full bg-cabinet-mute-1/60 rounded-sm"
              style={{ width: `${nationalPct}%` }}
            />
          </div>
          <span className="w-7 text-[11px] text-cabinet-mute-1 text-right flex-shrink-0">
            {Math.round(national)}
          </span>
        </div>
      </div>
    </li>
  );
}


// ── Composants partagés ──────────────────────────────────────────────────────


function WidgetCard({ kicker, title, subtitle, icon: Icon, testId, unavailable, children }) {
  return (
    <article
      className="bg-white border border-cabinet-mute-2/40 rounded-lg p-5 flex flex-col"
      data-testid={testId}
    >
      <header className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon size={12} className="text-cabinet-bordeaux/70" strokeWidth={1.8} />
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-cabinet-mute-1">
            {kicker}
          </span>
        </div>
        <h3 className="font-playfair text-[17px] text-cabinet-ink font-medium tracking-tight leading-snug">
          {title}
        </h3>
        <p className="mt-0.5 text-[11.5px] text-cabinet-mute-1 tracking-wide">
          {subtitle}
        </p>
      </header>

      <div className="flex-1 min-h-0">
        {unavailable ? (
          <WidgetEmpty
            message="Widget temporairement indisponible. Les autres signaux restent à jour."
            isWidgetUnavailable
          />
        ) : (
          children
        )}
      </div>
    </article>
  );
}


function WidgetEmpty({ message, isWidgetUnavailable }) {
  return (
    <p
      className={`text-[12.5px] leading-relaxed text-cabinet-mute-1 italic font-light px-3 py-5 text-center bg-cabinet-cream-warm rounded-md border ${
        isWidgetUnavailable ? "border-cabinet-gold/30" : "border-cabinet-mute-2/30"
      }`}
    >
      {message}
    </p>
  );
}


function PremiumEmptyState({ icon: Icon, title, message, testId }) {
  return (
    <div
      className="bg-cabinet-cream-warm border border-cabinet-mute-2/40 rounded-lg px-8 py-12 text-center"
      data-testid={testId}
    >
      <div className="inline-flex w-12 h-12 rounded-full bg-cabinet-bordeaux/8 items-center justify-center mb-4">
        <Icon size={20} className="text-cabinet-bordeaux" strokeWidth={1.5} />
      </div>
      <h3 className="font-playfair text-xl text-cabinet-ink font-medium tracking-tight">
        {title}
      </h3>
      <p className="mt-2.5 text-cabinet-navy text-[14px] leading-relaxed max-w-md mx-auto">
        {message}
      </p>
    </div>
  );
}


// ── Helpers ──────────────────────────────────────────────────────────────────


function _formatDateTime(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const datePart = new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
    }).format(d);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${datePart} à ${hh}h${mm}`;
  } catch {
    return iso;
  }
}


export default TendancesSection;
